import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lightning, EnvelopeSimple, LockKey } from "@phosphor-icons/react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Disclaimer } from "../components/Header";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0A0A0A] text-slate-50 noise-bg flex flex-col">
    <div className="flex-1 flex items-center justify-center px-4 py-16 relative z-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <Lightning size={28} weight="fill" className="text-[#00E676]" />
          <span className="font-display text-3xl font-black uppercase tracking-tight">Parlay<span className="text-[#00E676]">Labs</span></span>
        </Link>
        <div className="bg-[#141419] border border-white/10 rounded-xl p-6 sm:p-8">{children}</div>
      </div>
    </div>
    <Disclaimer />
  </div>
);

const inputCls = "w-full rounded-lg bg-black/40 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#2563EB] transition-colors";

export default function ForgotPassword() {
  const { API } = useAuth();
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`,
        { email, origin_url: window.location.origin });
      setResult(data);
    } catch (err) {
      setResult({ message: formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 className="font-display text-3xl font-black uppercase tracking-tight mb-2 flex items-center gap-2">
        <EnvelopeSimple size={26} weight="bold" className="text-[#2563EB]" /> Reset password
      </h1>
      <p className="text-sm text-slate-400 mb-6">Enter your email and we'll send you a reset link.</p>
      {result ? (
        <div data-testid="forgot-result">
          <p className="text-sm text-[#00E676] mb-4" data-testid="forgot-message">{result.message}</p>
          {result.dev_link && (
            <div className="rounded-lg bg-black/40 border border-[#FFD700]/30 p-3 mb-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#FFD700] mb-1">Dev mode — no email provider configured</p>
              <a href={result.dev_link} className="text-xs text-slate-300 underline break-all" data-testid="dev-reset-link">{result.dev_link}</a>
            </div>
          )}
          <Link to="/auth" className="text-sm font-semibold text-[#2563EB] hover:underline">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" data-testid="forgot-email-input" className={inputCls} />
          <button type="submit" disabled={busy} data-testid="forgot-submit-btn"
            className="w-full rounded-full bg-[#2563EB] text-white font-bold py-3 text-sm hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-50">
            {busy ? "Sending..." : "Send reset link"}
          </button>
          <Link to="/auth" className="block text-center text-sm text-slate-500 hover:text-white transition-colors">Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const { API } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: params.get("token") || "", new_password: password });
      setDone(true);
      setTimeout(() => navigate("/auth"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 className="font-display text-3xl font-black uppercase tracking-tight mb-2 flex items-center gap-2">
        <LockKey size={26} weight="bold" className="text-[#00E676]" /> New password
      </h1>
      {done ? (
        <p className="text-sm text-[#00E676]" data-testid="reset-success">Password updated — taking you to sign in...</p>
      ) : (
        <form onSubmit={submit} className="space-y-4 mt-4">
          <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="New password (min 6 characters)" data-testid="reset-password-input" className={inputCls} />
          {error && <p className="text-sm text-[#FF3366]" data-testid="reset-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="reset-submit-btn"
            className="w-full rounded-full bg-[#00E676] text-black font-bold py-3 text-sm hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-50">
            {busy ? "Updating..." : "Update password"}
          </button>
        </form>
      )}
    </Shell>
  );
}
